import { useState } from 'react';

import { HttpError, postForm, type FormValue } from '@/lib/http';

export type AuthPage =
  'sign-in' | 'sign-out' | 'forgot' | 'reset' | 'password-change' | 'sso-error' | 'sso-sign-out';

export interface AuthMessage {
  tone: string;
  text: string;
}

export interface AuthScreenProps {
  page: AuthPage;
  /** Django CSRF token — rendered as the hidden form field. */
  csrfToken: string;
  /** POST target; defaults to the current URL (Django re-renders on error). */
  action: string;
  logoUrl: string;
  /** ?next= passthrough for the sign-in flow. */
  next: string;
  /** Flash messages from Django's messages framework. */
  messages: AuthMessage[];
  /** Form errors from a failed submit (server re-render). */
  errors: string[];
  /** Prefill for username after a failed sign-in. */
  username: string;
  /** Reset flow: false when the reset link is invalid/expired. */
  resetLinkValid: boolean;
  /** Empty when password sign-in is not offered (sso_only). */
  signInUrl: string;
  forgotPasswordUrl: string;
  /** /sso/login/, or empty while SSO sign-in is off. */
  ssoLoginUrl?: string;
  /** The SSO frontend logout the sso-sign-out page continues to. */
  ssoLogoutUrl?: string;
}

/* The auth pages load no icon font (base_auth.html pulls only fonts.css and
   auth.css), so the reveal affordance ships as inline SVG. */
function RevealIcon({ off }: { off: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {off ? (
        <>
          <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.4 18.4 0 0 0 2 12s3 8 10 8a9.1 9.1 0 0 0 5.39-1.61" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <path d="m2 2 20 20" />
        </>
      ) : (
        <>
          <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

function Field({
  label,
  name,
  type = 'text',
  defaultValue = '',
  autoComplete,
  focusOnMount = false,
}: {
  label: string;
  name: string;
  type?: string;
  defaultValue?: string;
  autoComplete?: string;
  focusOnMount?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const inputId = `auth_${name}`;

  return (
    <div className="auth-field">
      <label className="auth-field__label" htmlFor={inputId}>
        {label}
      </label>
      <div className={`auth-field__control${isPassword ? ' auth-field__control--password' : ''}`}>
        <input
          id={inputId}
          name={name}
          type={isPassword && revealed ? 'text' : type}
          defaultValue={defaultValue}
          autoComplete={autoComplete}
          /* A revealed password is plain text; keep the assists that would
             otherwise mangle it switched off. */
          autoCapitalize={isPassword ? 'off' : undefined}
          autoCorrect={isPassword ? 'off' : undefined}
          spellCheck={isPassword ? false : undefined}
          required
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- single-form page */
          autoFocus={focusOnMount}
        />
        {isPassword ? (
          <button
            type="button"
            className="auth-field__reveal"
            onClick={() => setRevealed((current) => !current)}
            aria-label={revealed ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            aria-pressed={revealed}
            aria-controls={inputId}
          >
            <RevealIcon off={revealed} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

const PAGE_COPY: Record<AuthPage, { title: string; subtitle: string; submit: string }> = {
  'sign-in': {
    title: 'Sign in',
    subtitle: 'Access your courier operations dashboard.',
    submit: 'Sign in',
  },
  'sign-out': { title: 'Signed out', subtitle: 'You have been signed out safely.', submit: '' },
  forgot: {
    title: 'Forgot password',
    subtitle: 'Enter your username and we will email you a reset link.',
    submit: 'Send reset link',
  },
  reset: {
    title: 'Reset password',
    subtitle: 'Choose a new password for your account.',
    submit: 'Reset password',
  },
  'password-change': {
    title: 'Change password',
    subtitle: 'Update the password for your account.',
    submit: 'Update password',
  },
  'sso-error': {
    title: "Couldn't sign you in",
    subtitle: 'Swyft SSO sign-in did not complete.',
    submit: '',
  },
  'sso-sign-out': {
    title: 'Signing you out',
    subtitle: 'Ending your Swyft SSO session…',
    submit: '',
  },
};

const PAGES_WITHOUT_FORM: ReadonlySet<AuthPage> = new Set([
  'sign-out',
  'sso-error',
  'sso-sign-out',
]);

/**
 * Sign-in renders a real POST form (CSRF + next as hidden inputs) so Django's
 * session/redirect semantics stay untouched. The password flows (forgot,
 * reset, change) submit via fetch for inline validation — same endpoints,
 * which answer JSON to XHR and keep the classic flow for plain POSTs.
 */
export function AuthScreen({
  page,
  csrfToken,
  action,
  logoUrl,
  next,
  messages,
  errors,
  username,
  resetLinkValid,
  signInUrl,
  forgotPasswordUrl,
  ssoLoginUrl = '',
  ssoLogoutUrl = '',
}: AuthScreenProps) {
  const copy = PAGE_COPY[page];
  const showForm = !PAGES_WITHOUT_FORM.has(page) && (page !== 'reset' || resetLinkValid);
  const fetchSubmit = page === 'forgot' || page === 'reset' || page === 'password-change';
  const [busy, setBusy] = useState(false);
  const [localAlerts, setLocalAlerts] = useState<AuthMessage[]>([]);

  const onFetchSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const fields: Record<string, FormValue> = {};
    new FormData(formElement).forEach((value, key) => {
      if (key !== 'csrfmiddlewaretoken' && typeof value === 'string') fields[key] = value;
    });
    setBusy(true);
    setLocalAlerts([]);
    try {
      const body = await postForm<{ status: boolean; message?: string; redirect?: string | null }>(
        action,
        fields
      );
      setLocalAlerts([{ tone: 'success', text: body.message || 'Done.' }]);
      formElement.reset();
      if (page === 'reset' && body.redirect) {
        window.setTimeout(() => window.location.assign(body.redirect as string), 1200);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        const body = error.body as { message?: string; errors?: string[] } | null;
        const texts = body?.errors?.length ? body.errors : [body?.message || 'Request failed.'];
        setLocalAlerts(texts.map((text) => ({ tone: 'error', text })));
      } else {
        setLocalAlerts([{ tone: 'error', text: 'Network error — please try again.' }]);
      }
    }
    setBusy(false);
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-card__brand">
          {logoUrl ? <img src={logoUrl} alt="Swyft Courier" width="56" height="56" /> : null}
        </div>
        <h1 className="auth-card__title">{copy.title}</h1>
        <p className="auth-card__subtitle">{copy.subtitle}</p>

        {[...messages, ...localAlerts].map((message, index) => (
          <div
            key={index}
            className={`auth-alert ${message.tone.includes('error') ? 'auth-alert--error' : 'auth-alert--success'}`}
            role={message.tone.includes('error') ? 'alert' : 'status'}
          >
            {message.text}
          </div>
        ))}
        {errors.map((error, index) => (
          <div key={`e${index}`} className="auth-alert auth-alert--error" role="alert">
            {error}
          </div>
        ))}

        {page === 'sign-out' ? (
          <>
            <p className="auth-signout">See you soon 👋</p>
            <a className="auth-submit" href={signInUrl} style={{ textDecoration: 'none' }}>
              Sign back in
            </a>
          </>
        ) : null}

        {page === 'sso-error' ? (
          <>
            {ssoLoginUrl ? (
              <a className="auth-submit" href={ssoLoginUrl} style={{ textDecoration: 'none' }}>
                Try Swyft SSO again
              </a>
            ) : null}
            {signInUrl ? (
              <div className="auth-links">
                <a href={signInUrl}>Sign in with your courier password</a>
              </div>
            ) : null}
          </>
        ) : null}

        {page === 'sso-sign-out' && ssoLogoutUrl ? (
          <a className="auth-submit" href={ssoLogoutUrl} style={{ textDecoration: 'none' }}>
            Continue
          </a>
        ) : null}

        {page === 'reset' && !resetLinkValid ? (
          <div className="auth-links">
            <a href={forgotPasswordUrl}>Request a new reset link</a>
          </div>
        ) : null}

        {page === 'sign-in' && ssoLoginUrl ? (
          <>
            {/* A plain GET link: the SDK starts the PKCE flow and CSRF is not involved. */}
            <a className="auth-submit" href={ssoLoginUrl} style={{ textDecoration: 'none' }}>
              Sign in with Swyft SSO
            </a>
            <p className="auth-links">or use your courier password</p>
          </>
        ) : null}

        {showForm ? (
          <form
            method="POST"
            action={action}
            onSubmit={fetchSubmit ? (event) => void onFetchSubmit(event) : undefined}
          >
            <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
            {page === 'sign-in' ? (
              <>
                {next ? <input type="hidden" name="next" value={next} /> : null}
                <Field
                  label="Username"
                  name="username"
                  defaultValue={username}
                  autoComplete="username"
                  focusOnMount
                />
                <Field
                  label="Password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                />
                <div className="auth-meta">
                  <a href={forgotPasswordUrl}>Forgot password?</a>
                </div>
              </>
            ) : null}
            {page === 'forgot' ? (
              <Field label="Username" name="username" autoComplete="username" focusOnMount />
            ) : null}
            {page === 'reset' ? (
              <>
                <Field
                  label="New password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  focusOnMount
                />
                <Field
                  label="Confirm password"
                  name="confirm_password"
                  type="password"
                  autoComplete="new-password"
                />
              </>
            ) : null}
            {page === 'password-change' ? (
              <>
                <Field
                  label="Current password"
                  name="old_password"
                  type="password"
                  autoComplete="current-password"
                  focusOnMount
                />
                <Field
                  label="New password"
                  name="new_password1"
                  type="password"
                  autoComplete="new-password"
                />
                <Field
                  label="Confirm new password"
                  name="new_password2"
                  type="password"
                  autoComplete="new-password"
                />
              </>
            ) : null}
            <button className="auth-submit" type="submit" disabled={busy}>
              {busy ? 'Working…' : copy.submit}
            </button>
          </form>
        ) : null}

        {page === 'forgot' || (page === 'reset' && resetLinkValid) ? (
          <div className="auth-links">
            <a href={signInUrl}>Back to sign in</a>
          </div>
        ) : null}
      </div>
    </main>
  );
}
